import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { Overlay, Popover, Button } from 'react-bootstrap';

import useRichTextEditor from './useRichTextEditor';
import LinkInsertModal from './LinkInsertModal';

function RichTextComposer({
  value,
  onChange,
  placeholder = 'Compose a message...',
  disabled = false,
  id = null,
  hasAttachments = false,
  toolbarAccessory = null,
  children,
}) {
  const [showLinkModal, setShowLinkModal] = useState(false);
  const [showLinkPopover, setShowLinkPopover] = useState(false);
  const [initialLinkText, setInitialLinkText] = useState('');
  const [initialLinkUrl, setInitialLinkUrl] = useState('');
  const [isEditingLink, setIsEditingLink] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const savedSelectionRef = useRef(null);
  const linkButtonRef = useRef(null);
  const composerRef = useRef(null);
  const popoverTargetRef = useRef(null);
  const lastPointerDownInsideRef = useRef(0);

  // Drives the `is-collapsed` class so CSS (below md) can collapse the composer
  // to a single line when the user isn't interacting with it.
  const isCollapsed = !isFocused && !showLinkModal && !showLinkPopover;

  const handleComposerFocus = () => setIsFocused(true);
  const handleComposerPointerDownCapture = () => {
    lastPointerDownInsideRef.current = Date.now();
  };
  const handleComposerBlur = (event) => {
    // Stay focused if focus is moving to another element inside the composer (e.g. a toolbar button).
    if (event.relatedTarget && composerRef.current?.contains(event.relatedTarget)) return;
    // Mobile: a tap on a composer button blurs the editor with relatedTarget=null (touch taps
    // don't focus buttons), which would collapse the composer before the tap's click lands.
    if (Date.now() - lastPointerDownInsideRef.current < 400) return;
    setIsFocused(false);
  };

  // Mobile fallback: tapping a non-interactive element doesn't fire blur on
  // iOS/Android, so we listen for outside taps directly while focused.
  useEffect(() => {
    if (!isFocused) return undefined;
    const handleOutsidePointerDown = (event) => {
      if (composerRef.current?.contains(event.target)) return;
      setIsFocused(false);
    };
    document.addEventListener('pointerdown', handleOutsidePointerDown);
    return () => document.removeEventListener('pointerdown', handleOutsidePointerDown);
  }, [isFocused]);

  const captureSelectionAndOpenLink = () => {
    const selection = window.getSelection();
    const selectedText = selection.toString().trim();

    if (selection.rangeCount > 0) {
      savedSelectionRef.current = { range: selection.getRangeAt(0).cloneRange(), selectedText };
      setInitialLinkText(selectedText || '');
    } else {
      savedSelectionRef.current = null;
      setInitialLinkText('');
    }

    setIsEditingLink(false);
    setInitialLinkUrl('');
    setShowLinkModal(true);
  };

  const {
    editorRef,
    activeFormats,
    isEmpty,
    toggleBold,
    toggleItalic,
    toggleBulletList,
    toggleNumberedList,
    applyFormat,
    insertLink,
    editorProps,
  } = useRichTextEditor({
 value, onChange, onTriggerLink: captureSelectionAndOpenLink,
});

  const findEditorLinkAt = (startNode) => {
    if (!startNode || !editorRef.current) return null;
    const el = startNode.nodeType === 3 ? startNode.parentElement : startNode;
    const link = el?.closest?.('a');
    return link && editorRef.current.contains(link) ? link : null;
  };

  const handleModalInsert = (url, text) => {
    setShowLinkModal(false);

    editorRef.current?.focus();

    // Restore the saved selection/position
    const saved = savedSelectionRef.current;
    if (saved?.range) {
      const selection = window.getSelection();
      selection.removeAllRanges();
      selection.addRange(saved.range);

      if (saved.selectedText) {
        document.execCommand('delete');
      }
    }

    insertLink(url, text || saved?.selectedText || 'link');
    savedSelectionRef.current = null;
  };

  const selectLinkAtCursor = () => {
    const selection = window.getSelection();

    let node = selection?.anchorNode;
    while (node && node !== editorRef.current) {
      if (node.nodeName === 'A') {
        const range = document.createRange();
        range.selectNodeContents(node);
        selection.removeAllRanges();
        selection.addRange(range);
        return node;
      }
      node = node.parentNode;
    }

    return null;
  };

  const handleEditLink = () => {
    setShowLinkPopover(false);
    const linkEl = selectLinkAtCursor();
    if (!linkEl) return;

    savedSelectionRef.current = {
      range: window.getSelection().getRangeAt(0).cloneRange(),
      selectedText: linkEl.textContent,
    };

    setInitialLinkText(linkEl.textContent || '');
    setInitialLinkUrl(linkEl.href || '');
    setIsEditingLink(true);
    setShowLinkModal(true);
  };

  const handleRemoveLink = () => {
    setShowLinkPopover(false);
    selectLinkAtCursor();

    editorRef.current?.focus();

    applyFormat('unlink');
  };

  const handleLinkClick = () => {
    if (activeFormats.has('link')) {
      const link = findEditorLinkAt(window.getSelection()?.anchorNode);
      if (link) popoverTargetRef.current = link;
      setShowLinkPopover((prev) => !prev);
    } else {
      captureSelectionAndOpenLink();
    }
  };

  // Click directly on link text in the editor — open the same edit/remove popover the toolbar link button shows.
  const handleEditorClick = (event) => {
    editorProps.onClick(event);
    const link = findEditorLinkAt(event.target);
    if (link) {
      popoverTargetRef.current = link;
      setShowLinkPopover(true);
    }
  };

  const preventFocusLoss = (e) => e.preventDefault();

  const toolbarButtons = [
    {
      format: 'bold',
      icon: 'fa-bold',
      title: 'Bold (Ctrl+B)',
      onClick: toggleBold,
      onMouseDown: preventFocusLoss,
    },
    {
      format: 'italic',
      icon: 'fa-italic',
      title: 'Italic (Ctrl+I)',
      onClick: toggleItalic,
      onMouseDown: preventFocusLoss,
    },
    {
      format: 'link',
      icon: 'fa-link-simple',
      title: 'Insert Link (Ctrl+K)',
      onClick: handleLinkClick,
      onMouseDown: preventFocusLoss,
    },
    {
      format: 'bullet',
      icon: 'fa-list-ul',
      title: 'Bullet List',
      onClick: toggleBulletList,
      onMouseDown: preventFocusLoss,
    },
    {
      format: 'number',
      icon: 'fa-list-ol',
      title: 'Numbered List',
      onClick: toggleNumberedList,
      onMouseDown: preventFocusLoss,
    },
  ];

  return (
    <div
      ref={composerRef}
      className={classnames('rich-text-composer', { 'is-collapsed': isCollapsed, 'has-attachments': hasAttachments })}
      onFocus={handleComposerFocus}
      onBlur={handleComposerBlur}
      onPointerDownCapture={handleComposerPointerDownCapture}
    >
      <div className="composer-toolbar d-flex align-items-center p-2 gap-1">
        {toolbarButtons.map((btn) => (
          <button
            key={btn.format}
            ref={btn.format === 'link' ? linkButtonRef : undefined}
            type="button"
            className={`btn btn-sm composer-toolbar-btn ${activeFormats.has(btn.format) ? 'btn-secondary' : ''}`}
            onClick={btn.onClick}
            onMouseDown={btn.onMouseDown}
            title={btn.title}
            disabled={disabled}
          >
            <i className={`fa-solid ${btn.icon}`} />
          </button>
        ))}
        {toolbarAccessory && <span className="ms-auto">{toolbarAccessory}</span>}
      </div>

      <div
        ref={editorRef}
        id={id}
        role="textbox"
        aria-label="Message composer"
        aria-multiline="true"
        tabIndex={0}
        contentEditable={!disabled}
        {...editorProps}
        onClick={handleEditorClick}
        onKeyDown={editorProps.onKeyDown}
        data-placeholder={placeholder}
        className={`form-control rounded-0 border-0 overflow-auto ${isEmpty ? 'empty-editor' : ''}`}
        suppressContentEditableWarning
      />

      {/* Bottom action bar */}
      <div className="composer-actions d-flex justify-content-end align-items-center p-3 pt-2">{children}</div>

      <LinkInsertModal
        show={showLinkModal}
        onHide={() => setShowLinkModal(false)}
        onInsert={handleModalInsert}
        initialText={initialLinkText}
        initialUrl={initialLinkUrl}
        title={isEditingLink ? 'Edit Link' : 'Insert Link'}
      />

      <Overlay
        target={popoverTargetRef.current || linkButtonRef.current}
        show={showLinkPopover}
        placement="right"
        rootClose
        onHide={() => setShowLinkPopover(false)}
      >
        <Popover>
          <Popover.Body className="d-flex gap-2 p-2">
            <Button variant="outline-primary" size="sm" onClick={handleEditLink}>
              <i className="fa-solid fa-pen me-1" /> Edit
            </Button>
            <Button variant="outline-danger" size="sm" onClick={handleRemoveLink}>
              <i className="fa-solid fa-link-slash me-1" /> Remove
            </Button>
          </Popover.Body>
        </Popover>
      </Overlay>
    </div>
  );
}

RichTextComposer.propTypes = {
  value: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  placeholder: PropTypes.string,
  disabled: PropTypes.bool,
  id: PropTypes.string,
  hasAttachments: PropTypes.bool,
  toolbarAccessory: PropTypes.node,
  children: PropTypes.node,
};

export default RichTextComposer;
